import {useEffect, useCallback, useState} from 'react'
import {useInView, type IntersectionOptions} from 'react-intersection-observer'
import type {makeClient} from '@roundforest/ga-commons'
import type {Product} from '~/types'
import {gaClient} from '~/utils/ga/gtag.client'
import {readCookie} from '~/utils/commons'
import {useAnalyticsConsent} from './use-consent'

const FACEBOOK_REPORT_BROWSE_NODE_DEPTH = 4
const ENGAGEMET_TIME_2_SECONDS_KEY = 'FsNgCMSviOwZEJaDu9ID'
const ENGAGEMET_TIME_10_SECONDS_KEY = 'WodgCMGviOwZEJaDu9ID'

export function useReportWidgetSeen(
  {
    widget_name,
    widget_place,
    trigger_type,
    variation,
    event_category,
    count_dws_items,
    count_products,
  }: WidgetSeenEventPayload,
  config?: IntersectionOptions,
) {
  const {optOutRejected} = useAnalyticsConsent()

  const [ref] = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      if (inView) {
        gaClient?.sendWidgetSeen({
          widget_name,
          widget_place,
          trigger_type,
          variation,
          event_category,
          count_dws_items,
          count_products,
        })
      }
    },
    ...config,
  })

  return [ref]
}

export function useReportSendSearch() {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(
    ({searchTerm}: useReportSendSearchProps) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      gaClient?.sendSearch({
        search_term: searchTerm,
      })
    },
    [optOutRejected],
  )
}

export function useReportItemSelected() {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(
    ({index, type, listId}: useReportItemSelectedProps) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      gaClient?.sendSelectContent({
        content_index: index,
        content_type: type,
        list_id: listId,
      })
    },
    [optOutRejected],
  )
}
export function useReportCustomEvent() {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(
    (name: string, payload: Record<string, string | number>) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      gaClient?.sendCustomEvent(name, payload)
    },
    [optOutRejected],
  )
}

export function useReportWidgetClick() {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(
    ({widget_name, widget_place, trigger_type, clicked_element, variation}: UseReportWidgetClickCallbackProps) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      gaClient?.sendWidgetClicked({
        widget_name,
        widget_place,
        trigger_type,
        clicked_element,
        variation,
      })
    },
    [optOutRejected],
  )
}

export function useReportItemSeen(
  {
    item_list_id,
    item_name,
    item_source,
    partner_item_brand,
    partner_item_id,
    affiliation,
    currency,
    is_dws,
    price,
    click_element_name,
    index,
    variation,
  }: Parameters<ReturnType<typeof makeClient>['sendViewItem']>[0],
  config?: IntersectionOptions,
) {
  const {optOutRejected} = useAnalyticsConsent()
  const [ref] = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      if (inView) {
        gaClient.sendViewItem({
          item_list_id,
          item_name,
          item_source,
          partner_item_brand,
          partner_item_id,
          affiliation,
          currency,
          is_dws,
          price,
          click_element_name,
          index,
          variation,
        })
      }
    },
    ...config,
  })

  return [ref]
}

export function useSendPartnerProductClickout({
  product,
  index,
  listName,
  locale,
  item_variant,
  categorySlug,
  categoryName,
  clientTraffic,
}: useSendPartnerProductClickoutParameters) {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(() => {
    const requested = readCookie('sharing_policy')
    if (requested || optOutRejected) return
    gaClient?.sendPartnerClickout({
      index: index,
      item_list_id: categorySlug,
      item_name: product.name,
      item_brand: product.brand,
      item_category: categoryName,
      item_variant,
      partner_item_id: product.asin,
      partner_item_brand: product.brand || 'unknown',
      click_element_name: listName,
      price: product.price?.amount,
      currency: product.price?.currency,
      discount: product.coupon?.value,
      location_id: locale,
      affiliation: product.affiliateLink.type,
      target: product.affiliateLink.url,
      traffic: clientTraffic ?? 'unknown',
      is_dws: product.isSellerProduct,
      dws_type: undefined,
      gclid: undefined,
      sys_id: undefined,
      click_id: product.affiliateLink.clickId,
    })

    gaClient?.reportForRemarketingAddToCart({
      slug: categorySlug,
    })

    if (!optOutRejected && window?.uetq) {
      window.uetq.push('event', 'ClickOut', {
        event_label: 'General',
        event_value: 0,
        event_category: 'Engagements',
      })
    }

    if (!optOutRejected && window?.fbq) {
      window.fbq('track', 'AddToCart', {
        destination: product.affiliateLink.type,
        is_sponsored: product.isSellerProduct,
        list_category_tree: convertBrowseNodeToString({browseNode: product.browseNode}),
      })
    }
  }, [categorySlug, categoryName, clientTraffic, index, product, listName, locale, item_variant, optOutRejected])
}

export function useSendPartnerProductClickoutCustom() {
  const {optOutRejected} = useAnalyticsConsent()
  return useCallback(
    (args: Parameters<ReturnType<typeof makeClient>['sendPartnerClickout']>[0]) => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      gaClient?.sendPartnerClickout(args)
    },
    [optOutRejected],
  )
}

export function useReportPageInfo({
  isMetacriticList,
  listSource,
  slug,
  numberOfDwsItemsInMainList,
  numberOfDwsItemsInOtherLists,
  numberOfItemsInMainList,
  numberOfItemsInOtherLists,
  amazonBrowseNodeId,
  amazonBrowseNodeName,
  forcedPosition,
  position,
  asin,
  variation,
}: {
  isMetacriticList: boolean
  slug: string
  listSource?: string
  numberOfDwsItemsInMainList: number
  numberOfDwsItemsInOtherLists: number
  numberOfItemsInMainList: number
  numberOfItemsInOtherLists: number
  amazonBrowseNodeId?: string
  amazonBrowseNodeName?: string
  forcedPosition?: boolean
  position?: string | null
  asin?: string | null
  variation?: string
}) {
  const {optOutRejected} = useAnalyticsConsent()
  const [ref] = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView) {
        const requested = readCookie('sharing_policy')
        if (requested || optOutRejected) return
        gaClient?.sendPageInfoEvent({
          metacritic_list: isMetacriticList ? 'true' : 'false',
          listSource,
          screen_size: `${screen.width}x${screen.height}`,
          forcedPosition,
          position,
          asin,
          variation,
        })
        gaClient?.sendPageItemsInfo({
          count_dws_items_in_list: numberOfDwsItemsInMainList,
          count_dws_items_in_non_list: numberOfDwsItemsInOtherLists,
          count_items_in_list: numberOfItemsInMainList,
          count_items_in_non_list: numberOfItemsInOtherLists,
          amazon_browse_node_id: amazonBrowseNodeId,
          amazon_browse_node_name: amazonBrowseNodeName,
        })
        gaClient?.reportForRemarketingViewItem({slug})
      }
    },
  })
  return [ref]
}

export const useReportAnalyticsEngaging = (googleAdsKey?: string) => {
  const [isEligibleForEvent, setIsEligibleForEvent] = useState(true)
  const {optOutRejected} = useAnalyticsConsent()
  useEffect(() => {
    let fiveSecondsTimer: ReturnType<typeof setTimeout> | null = null
    let tenSecondsTimer: ReturnType<typeof setTimeout> | null = null

    const resetTimers = () => {
      if (fiveSecondsTimer) {
        clearTimeout(fiveSecondsTimer)
        fiveSecondsTimer = null
      }
      if (tenSecondsTimer) {
        clearTimeout(tenSecondsTimer)
        tenSecondsTimer = null
      }
    }

    const handleFocus = () => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      if (!isEligibleForEvent) return

      fiveSecondsTimer = setTimeout(() => {
        if (isEligibleForEvent) {
          window?.gtag?.('event', 'conversion', {
            send_to: `${googleAdsKey}/${ENGAGEMET_TIME_2_SECONDS_KEY}`,
          })
          gaClient?.sendCustomEvent('bounce2', {})
        }
      }, 2000)

      tenSecondsTimer = setTimeout(() => {
        if (isEligibleForEvent) {
          window?.gtag?.('event', 'conversion', {
            send_to: `${googleAdsKey}/${ENGAGEMET_TIME_10_SECONDS_KEY}`,
          })
          gaClient?.sendCustomEvent('bounce10', {})
        }
      }, 10000)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        resetTimers()
        setIsEligibleForEvent(false)
      }
    }

    const handleBlur = () => {
      resetTimers()
      setIsEligibleForEvent(false)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    handleFocus()

    return () => {
      resetTimers()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [isEligibleForEvent, googleAdsKey, optOutRejected])

  return null
}
export const useReportClickoutToSiteTime = () => {
  const {optOutRejected} = useAnalyticsConsent()
  useEffect(() => {
    const requested = readCookie('sharing_policy')
    if (requested || optOutRejected) return
    let timer: ReturnType<typeof setTimeout>

    const handleFocus = () => {
      const requested = readCookie('sharing_policy')
      if (requested || optOutRejected) return
      if (localStorage?.getItem('toSiteTime')) return

      if (document.visibilityState === 'visible' && localStorage?.getItem('isEligibleForSiteReturnEvent')) {
        const clickTime = localStorage?.getItem('clickTime') ?? '0'
        const now = Date.now()
        const elapsedTime = Math.round((now - parseInt(clickTime, 10)) / 1000)
        gaClient?.sendCustomEvent('clickout_to_site_time', {
          clickoutToSiteTimeSec: elapsedTime,
        })
        localStorage?.setItem('toSiteTime', elapsedTime.toString())
      }

      timer = setTimeout(() => {
        if (localStorage?.getItem('clickTime')) {
          localStorage?.setItem('isEligibleForSiteReturnEvent', 'true')
        }
      }, 1000)
    }
    const handleBeforeUnload = () => {
      localStorage.removeItem('clickTime')
      localStorage.removeItem('isEligibleForSiteReturnEvent')
      localStorage.removeItem('toSiteTime')
    }

    document.addEventListener('visibilitychange', handleFocus)
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('focus', handleFocus)
    handleFocus()

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', handleFocus)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [optOutRejected])

  return null
}

function convertBrowseNodeToString({browseNode}: {browseNode?: any}) {
  const browseNodeNames = [
    ...(browseNode?.ancestors?.map(({name}: any) => name)?.reverse() ?? []),
    browseNode?.name,
  ].filter((name) => name)

  if (browseNodeNames.length) {
    return browseNodeNames.slice(0, FACEBOOK_REPORT_BROWSE_NODE_DEPTH).join('__')
  }
  return undefined
}

export type WidgetSeenEventPayload = {
  widget_name: string
  widget_place: string
  trigger_type?: string
  count_products?: number
  count_dws_items?: number
  event_category?: string
  variation?: string
}
export type useReportItemSelectedProps = {
  index: Parameters<ReturnType<typeof makeClient>['sendSelectContent']>[0]['content_index']
  type: Parameters<ReturnType<typeof makeClient>['sendSelectContent']>[0]['content_type']
  listId: Parameters<ReturnType<typeof makeClient>['sendSelectContent']>[0]['list_id']
}
export type useReportSendSearchProps = {
  searchTerm: Parameters<ReturnType<typeof makeClient>['sendSearch']>[0]['search_term']
}
export type SeenItemEventPayload = {
  item_list_id: string
  partner_item_id: string
  partner_item_brand: string
  item_name: string
  affiliation: string
  placement_id?: string
  index: number
  item_source: 'widget' | 'list'
  widget_name?: string
  price?: number
  currency?: string
  seller?: string
  click_element_name?: string | undefined
  is_dws: boolean
  event_category?: string
  variation?: string
}

export type useSendPartnerProductClickoutParameters = {
  product: Product
  index: number
  listName: string
  categorySlug: string
  categoryName: string
  locale?: string
  clientTraffic?: string
  item_variant?: 'extension-direct' | 'extension'
}

export type AbTestEventPayload = {
  ab_test_name: string
  ab_test_value: string
}

export type WebVitalsEventPayload = {
  metric_name: string
  delta: number
  value: number
  id: string
  rating: string
}

export interface UseReportWidgetClickCallbackProps {
  variation?: Parameters<ReturnType<typeof makeClient>['sendWidgetClicked']>[0]['variation']
  widget_name: Parameters<ReturnType<typeof makeClient>['sendWidgetClicked']>[0]['widget_name']
  widget_place: Parameters<ReturnType<typeof makeClient>['sendWidgetClicked']>[0]['widget_place']
  trigger_type?: Parameters<ReturnType<typeof makeClient>['sendWidgetClicked']>[0]['trigger_type']
  clicked_element?: Parameters<ReturnType<typeof makeClient>['sendWidgetClicked']>[0]['clicked_element']
}
